/**
 * ReadOnlyGuard — soft app-wide interception of write actions when the
 * tenant's subscription is expired (`write_allowed=false`).
 *
 * Goals
 * -----
 * 1. **Hover tooltip** on every save / submit / create button with clear
 *    copy ("You are in read-only mode — renew your plan to make changes")
 *    and a "Renew now" link that navigates to the plan-management page.
 * 2. **Intercept the click** at the capture phase BEFORE the button's own
 *    handler runs so the API is never called and the tenant never sees
 *    a 403. Tooltip pulses so the block feels tactile, not silent.
 * 3. **Escape hatches** for the exact surfaces the tenant needs to keep
 *    working: the plan-selection modal, the downgrade modal, the pay-
 *    invoice buttons on `MySubscriptionPage`, and the top-of-app renew
 *    banner. Marked via `data-readonly-safe="true"` on any ancestor.
 * 4. **Zero touches to individual buttons** — heuristic-based match on
 *    `data-testid` OR button text (verb list) so we don't need to edit
 *    200+ components across the app.
 *
 * Non-goals
 * ---------
 * * The guard is a *safety net*. Backend enforcement still returns 403
 *   for any write that slips through (subscription_write_guard middleware).
 * * The guard does NOT prevent reads, exports, or navigation.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { useSubscription } from '../SubscriptionContext';
import { useAuth } from '../AuthContext';

// Match a superset of write-action button ids. Anything in this regex is
// treated as a write button.  Keep the pattern liberal — a false positive
// (blocking a benign button) is worse UX than a false negative (letting a
// write through to the backend, which the write-guard middleware catches
// anyway with a friendlier 403).
const TESTID_RE = /(save|submit|create|register|record|approve|dispatch|receive|fulfil?l|confirm|update|delete|remove|invoice-add|invoice-create|new-invoice|new-appointment|new-patient|add-patient|add-appointment|add-serial|add-product|add-vendor|book-|schedule-|generate-|allocate|adopt|start-test|start-session|pay-|checkout|refund|issue-|apply-advance|write-off|cancel-invoice|complete|resolve|finalise|finalize|assign)/i;

const EXPORT_RE = /(export|download|print|pdf|csv|excel)/i;

// Verb list scanned against button innerText — plain-English fallback.
// Case-insensitive whole-word match against the first ~40 chars of the
// button's text so long menu items don't false-positive.
const VERB_RE = /^(save|submit|create|add|new|register|record|approve|dispatch|receive|fulfil|fulfill|confirm|update|delete|edit|remove|book|schedule|generate|assign|allocate|adopt|start|pay|check ?out|refund|issue|apply advance|write.?off|cancel invoice|complete|resolve|finalise|finalize|send|invoice|estimate|checkout|renew)\b/i;

// Surfaces that must remain interactive so the tenant can still pay to
// unlock. Any ancestor with `data-readonly-safe="true"` short-circuits
// the guard. We also always allow the subscription banner CTA.
function isEscapeHatch(el) {
  if (!el || el.nodeType !== 1) return false;
  return !!el.closest('[data-readonly-safe="true"]')
      || !!el.closest('[data-testid="subscription-banner-cta"]')
      || !!el.closest('[data-testid="subscription-banner"]')
      || !!el.closest('[data-testid="plan-selection-modal"]')
      || !!el.closest('[data-testid="change-plan-modal"]')
      || !!el.closest('[data-testid="topbar-user-menu"]');
}

function isWriteButton(el) {
  if (!el || el.nodeType !== 1) return false;
  // Only capture buttons + submit inputs. Anchors are navigation — skip.
  const tag = (el.tagName || '').toLowerCase();
  const type = (el.getAttribute && el.getAttribute('type') || '').toLowerCase();
  const isBtn = tag === 'button' || (tag === 'input' && (type === 'submit' || type === 'button'));
  if (!isBtn) return false;

  const testid = (el.getAttribute && el.getAttribute('data-testid')) || '';
  // Data export / print / download must always stay available.
  if (EXPORT_RE.test(testid) || EXPORT_RE.test((el.innerText || '').slice(0, 60))) return false;
  if (testid && TESTID_RE.test(testid)) return true;

  // Fallback — strip leading punctuation/whitespace so "+ New Invoice"
  // and "› Save" both match. Only inspect first ~60 chars.
  const text = (el.innerText || '').replace(/^[\s+›·»→\-—•·:*]+/, '').trim().slice(0, 60);
  if (text.length < 2) return false;
  return VERB_RE.test(text);
}

// Finds the nearest interactive button ancestor from `target` up to `root`.
function closestButton(target) {
  let el = target;
  while (el && el.nodeType === 1) {
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'button' || tag === 'input') return el;
    el = el.parentElement;
  }
  return null;
}

export default function ReadOnlyGuard() {
  const sub = useSubscription();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [tip, setTip] = useState(null);   // {x, y, targetRect}
  const [pulse, setPulse] = useState(false);
  const clearTimer = useRef(null);

  // Active only when the current tenant is in expired status AND user is
  // NOT a founder / super_admin (they bypass write-guard entirely on the
  // backend, so we mirror the same bypass on the frontend to avoid an
  // annoying tooltip for platform admins).
  const roleBypass = user && ['founder', 'super_admin'].includes(user.role);
  const active = !!sub?.v2 && sub.v2.write_allowed === false && !roleBypass;

  const showFor = useCallback((btn) => {
    if (!btn) { setTip(null); return; }
    const rect = btn.getBoundingClientRect();
    if (clearTimer.current) { clearTimeout(clearTimer.current); clearTimer.current = null; }
    // Position tooltip 8px above the button, centered horizontally.
    setTip({
      x: rect.left + rect.width / 2,
      y: rect.top,
      rect,
    });
  }, []);

  const hide = useCallback(() => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setTip(null), 120);
  }, []);

  // Hover delegation → mouseover on document with capture=false.
  // We intentionally use mouseover (bubbles) rather than mouseenter for
  // delegation — mouseenter doesn't bubble.
  useEffect(() => {
    if (!active) { setTip(null); return; }
    const onOver = (e) => {
      const btn = closestButton(e.target);
      if (!btn) return;
      if (isEscapeHatch(btn)) return;
      if (!isWriteButton(btn)) return;
      showFor(btn);
    };
    const onOut = (e) => {
      // If leaving to a non-button descendant, keep tooltip. If leaving to
      // outside the button entirely, hide.
      const btn = closestButton(e.target);
      if (!btn) return;
      const relTarget = e.relatedTarget;
      if (relTarget && btn.contains(relTarget)) return;
      hide();
    };
    document.addEventListener('mouseover', onOver, true);
    document.addEventListener('mouseout',  onOut,  true);
    return () => {
      document.removeEventListener('mouseover', onOver, true);
      document.removeEventListener('mouseout',  onOut,  true);
    };
  }, [active, showFor, hide]);

  // Click interception at capture phase — runs BEFORE the button's own
  // onClick, so the API call is never fired.
  useEffect(() => {
    if (!active) return;
    const onClick = (e) => {
      const btn = closestButton(e.target);
      if (!btn) return;
      if (isEscapeHatch(btn)) return;
      if (!isWriteButton(btn)) return;
      // Block the write.
      e.preventDefault();
      e.stopPropagation();
      // (Firefox/Safari need stopImmediatePropagation when other capture-
      //  listeners are also attached.)
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      showFor(btn);
      setPulse(true);
      setTimeout(() => setPulse(false), 500);
    };
    document.addEventListener('click',       onClick, true);
    document.addEventListener('submit',      onClick, true);
    return () => {
      document.removeEventListener('click',  onClick, true);
      document.removeEventListener('submit', onClick, true);
    };
  }, [active, showFor]);

  // Hide tip on route change so it doesn't linger over a stale position.
  useEffect(() => {
    setTip(null);
  }, [location.pathname]);

  if (!active || !tip) return null;

  // Tooltip clamped to viewport with a subtle arrow.
  const TIP_W = 260;
  const PADDING = 12;
  const vw = window.innerWidth;
  const left = Math.max(PADDING, Math.min(vw - TIP_W - PADDING, tip.x - TIP_W / 2));
  const top = Math.max(8, tip.y - 8);

  return (
    <div
      data-testid="read-only-tooltip"
      className={`fixed z-[300] pointer-events-auto transition-all ${pulse ? 'animate-pulse' : ''}`}
      style={{
        left,
        top,
        width: TIP_W,
        transform: 'translateY(-100%)',
      }}
      role="tooltip"
    >
      <div className="rounded-lg shadow-xl border border-rose-300 bg-rose-50 px-3 py-2.5 text-[12.5px] text-rose-900 leading-snug">
        <div className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-md bg-rose-200 text-rose-800 flex items-center justify-center shrink-0 mt-0.5">
            <Lock size={11} strokeWidth={2.5} />
          </span>
          <div>
            <b className="font-bold">You are in read-only mode</b>
            <div className="mt-0.5">Renew your plan to make changes.</div>
            <button
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                navigate('/billing/my-subscription');
                setTip(null);
              }}
              data-testid="read-only-tooltip-renew"
              className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-bold text-white bg-rose-600 hover:bg-rose-700 px-2 py-1 rounded-md shadow-sm"
            >
              Renew now <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </div>
      {/* Little downward arrow */}
      <div
        className="absolute w-3 h-3 bg-rose-50 border-r border-b border-rose-300 rotate-45"
        style={{ left: Math.min(TIP_W - 20, Math.max(10, tip.x - left - 6)), bottom: -6 }}
      />
    </div>
  );
}
